import {GuideStateApi} from "@khan/guide-state";

/**
 * Promise that resolves on the completion of the final pending guide request.
 * This may be a chain of multiple promises if there are multiple requests
 * queued in a row.
 */
let lastGuideRequestCompletion: Promise<void> = Promise.resolve();

/**
 * Wait until no other guide requests are loading, and until all previous calls
 * to waitForPendingGuideRequests have completed. Returns a promise that resolves with a
 * "done" function when it's safe to make a guide request.
 *
 * The caller should call done() to signal completion when the request is
 * finished (typically in a finally block).
 *
 */
export function waitForPendingGuideRequests(): Promise<() => void> {
    // We return a promise that resolves to doneCallback and we expect that
    // when doneCallback is called by the caller, it signals that the caller's
    // request is done and we can move on the to the next chained promise, if
    // any. That's why we make doneCallback resolve the currentRequestPromise.
    let doneCallback: () => void;
    const currentRequestPromise = new Promise<void>((resolve) => {
        doneCallback = resolve;
    });

    // Append to the queue of existing requests by attaching onto the promise
    // chain. The lockPromise is what the caller will await on, and it will
    // resolve once the caller can go ahead and start their request.
    const lockPromise = lastGuideRequestCompletion.then(() => {
        // Update the global chain to point to this request's completion
        lastGuideRequestCompletion = currentRequestPromise;

        return new Promise<() => void>((resolve) => {
            if (!GuideStateApi.latest().isLoading) {
                // Not currently loading, so it's safe to proceed right away.
                resolve(doneCallback);
                return;
            }

            // Something is currently loading, so we wait for isLoading to
            // become false before proceeding.
            const handleChange = (key: string) => {
                if (key === "isLoading" && !GuideStateApi.latest().isLoading) {
                    unsubscribe();
                    resolve(doneCallback);
                }
            };
            const unsubscribe = GuideStateApi.on("changed", handleChange);
        });
    });

    return lockPromise;
}
